<script lang="ts" setup>
import { countries, segmentation, segmentationV1 } from '@thecompaniesapi/shared/client'

const { getCompanyAttributes, getMetaAttributes, getSortAttributes } = useApiAttributes()
const { refreshResponseWithApiVersion, version } = useApiDoc()
const { t } = useI18n()
const api = useApi()
const localePath = useLocalePath()

const { data: response, execute, status } = useAsyncData(
  'doc:companies-search-name',
  () => {
    const name = version.value === 'v1' ? segmentationV1.freeQueries.name : segmentation.freeQueries.name

    return api.companies.fetchByName({ name })
  },
  { immediate: false },
)

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: '/companies/by-name',
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.name'),
    example: `…/companies/by-name?name=${segmentation.freeQueries.name}`,
    name: 'name',
    required: true,
    type: 'string',
  },
  {
    description: t('attributes.page'),
    example: '/companies/by-name?page=1',
    name: 'page',
    optional: true,
    type: 'number',
  },
  {
    description: t('attributes.countries'),
    example: '/companies/by-name?countries=us,fr,de',
    name: 'countries',
    optional: true,
    type: 'string',
    values: countries
      .filter(country => !['bq', 'xk'].includes(country.code))
      .map((country): ApiAttributeValueObject => ({
        flag: country.code,
        label: country.code,
        tooltip: t(`locations.countries.${country.code}`),
      })),
  },
  {
    description: t('attributes.exactWordsMatch'),
    example: '/companies/by-name?exactWordsMatch=false',
    name: 'exactWordsMatch',
    optional: true,
    type: 'boolean',
  },
  {
    description: t('attributes.listsToExclude'),
    example: '/companies/by-name?listsToExclude=3920,3925',
    name: 'listsToExclude',
    optional: true,
    type: 'string',
  },
  {
    description: t('attributes.searchFields'),
    example: '/companies?searchFields[]=domain&searchFields[]=name',
    name: 'searchFields',
    optional: true,
    type: 'array',
    values: segmentation.attributesWeCanSearch,
  },
  {
    description: t('attributes.simplified'),
    example: '/companies/by-name?simplified=true',
    name: 'simplified',
    optional: true,
    type: 'boolean',
    values: ['false', 'true'],
  },
  {
    description: t('attributes.size'),
    example: '/companies/by-name?name=amazon&size=2',
    name: 'size',
    optional: true,
    type: 'number',
  },
  ...getSortAttributes({ endpoint: '/companies/by-name' }),
]

const responseAttributes = computed((): ApiAttribute[] => {
  return [
    {
      attributes: getCompanyAttributes({ version: version.value }),
      description: t('response.companies'),
      link: {
        label: t('globals.browseAllDatapoints'),
        to: localePath({
          name: 'api-slug',
          params: { slug: 'datapoints' },
        }),
      },
      name: 'companies',
      type: 'array',
    },
    getMetaAttributes(),
  ]
})

async function onVisibilityChange(visibility: boolean) {
  refreshResponseWithApiVersion({
    api,
    execute,
    response,
    visibility,
  })
}
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "countries": "The countries to filter the companies by. You can provide multiple countries separated by a comma. Default to all countries.",
      "exactWordsMatch": "If set to true, the company name must match the searched words. 'Bank of A' will not return Bank of America but 'Bank of' and 'Bank of America' will. Default to true.",
      "listsToExclude": "Comma-separated IDs of your lists containing companies to exclude from the results.",
      "name": "The company name.",
      "page": "The page to fetch (default to 1).",
      "simplified": "Return a simplified version of the companies (no credits deducted but limitation are applied on the free plan). It is useful for previewing the data before fetching the full version. Default to false.",
      "size": "The number of companies to be returned (between 1 and 25). Default to 1."
    },
    "response": {
      "companies": "The companies that match with the name."
    }
  },
  "es": {
    "attributes": {
      "countries": "Los países para filtrar las empresas. Puedes proporcionar varios países separados por comas. Por defecto a todos los países.",
      "exactWordsMatch": "Si se establece en true, el nombre de la empresa debe coincidir con las palabras buscadas. 'Bank of A' no devolverá Bank of America, pero 'Bank of' y 'Bank of America' lo harán. Por defecto a true.",
      "listsToExclude": "IDs de tus listas separados por comas que contienen las empresas a excluir de los resultados.",
      "name": "El nombre de la empresa.",
      "page": "La página a recuperar (por defecto 1).",
      "simplified": "Devuelve una versión simplificada de las empresas (no se deduce ningún crédito, pero se aplican limitaciones en el plan gratuito). Es útil para visualizar los datos antes de recuperar la versión completa. Por defecto a false.",
      "size": "El número de empresas a devolver (entre 1 y 25). Por defecto a 1."
    },
    "response": {
      "companies": "Las empresas que coinciden con el nombre."
    }
  },
  "fr": {
    "attributes": {
      "countries": "Les pays pour filtrer les entreprises. Vous pouvez fournir plusieurs pays séparés par une virgule. Par défaut à tous les pays.",
      "exactWordsMatch": "Si défini à true, le nom de l'entreprise doit correspondre aux mots recherchés. 'Bank of A' ne retournera pas Bank of America mais 'Bank of' et 'Bank of America' le feront. Par défaut à true.",
      "listsToExclude": "Les identifiants séparés par des virgules de vos listes contenant les entreprises à exclure des résultats.",
      "name": "Le nom de l'entreprise.",
      "page": "La page à récupérer (par défaut 1).",
      "simplified": "Retourne une version simplifiée des entreprises (aucun crédit déduit mais des limitations sont appliquées sur le plan gratuit). C'est utile pour prévisualiser les données avant de récupérer la version complète. Par défaut à false.",
      "size": "Le nombre d'entreprises à retourner (entre 1 et 25). Par défaut à 1."
    },
    "response": {
      "companies": "Les entreprises qui correspondent au nom."
    }
  }
}
</i18n>
